import { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import productsService from '../services/productsService';
import Icon from './ui/AppIcon';

export default function ProductSearch({ mobile = false, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('idle');
  const [selected, setSelected] = useState(-1);
  const term = query.trim();

  useEffect(() => {
    setQuery(new URLSearchParams(location.search).get('search') || '');
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open || !term) return;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const products = await productsService.getSuggestions(term, controller.signal);
        if (!controller.signal.aborted) {
          setItems(products);
          setStatus('ready');
        }
      } catch {
        if (!controller.signal.aborted) setStatus('error');
      }
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, open]);

  const go = (product) => {
    setOpen(false);
    navigate(product ? `/products/${product._id}` : `/products?search=${encodeURIComponent(term)}`);
    onNavigate?.();
  };

  const clearQuery = () => {
    setQuery('');
    setItems([]);
    setSelected(-1);
    setStatus('idle');
    setOpen(false);
    inputRef.current?.focus();
  };

  const show = open && Boolean(term);
  const focusSearch = () => {
    if (!open) {
      setItems([]);
      setStatus('loading');
      setSelected(-1);
    }
    setOpen(true);
  };

  const highlight = (name) => {
    const start = name.toLowerCase().indexOf(term.toLowerCase());
    if (start < 0) return name;
    return (
      <>
        {name.slice(0, start)}
        <strong className="font-bold text-[#003087]">{name.slice(start, start + term.length)}</strong>
        {name.slice(start + term.length)}
      </>
    );
  };

  return (
    <form
      className={
        mobile
          ? 'relative w-full'
          : 'relative hidden md:flex flex-1 max-w-2xl lg:max-w-3xl mx-3 lg:mx-6'
      }
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (term) go(show && selected >= 0 ? items[selected] : undefined);
      }}
    >
      <div className={mobile ? 'site-header-mobile-search' : 'flex w-full'}>
        {mobile && (
          <span className="site-header-mobile-search__leading" aria-hidden="true">
            <Icon name="MagnifyingGlassIcon" size={18} />
          </span>
        )}
        <input
          ref={inputRef}
          type="search"
          enterKeyHint="search"
          role="combobox"
          aria-label="Search products"
          aria-autocomplete="list"
          aria-expanded={show}
          aria-controls={show ? listId : undefined}
          aria-activedescendant={show && selected >= 0 ? `${listId}-${selected}` : undefined}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={mobile ? 'Search products…' : 'Search foam cups, foil pans, containers...'}
          value={query}
          onFocus={focusSearch}
          onChange={(event) => {
            setQuery(event.target.value);
            setItems([]);
            setSelected(-1);
            setStatus('loading');
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              if (query) clearQuery();
              else {
                setOpen(false);
                setSelected(-1);
              }
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              if (!open) {
                focusSearch();
                return;
              }
              if (items.length) {
                setSelected(
                  (index) => (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
                );
              }
            }
          }}
          className={
            mobile
              ? 'site-header-mobile-search__input'
              : 'flex-1 min-w-0 px-3 lg:px-5 py-2 lg:py-3 text-sm border-0 outline-none rounded-l-lg text-gray-800 placeholder-gray-400 bg-white'
          }
        />
        {mobile && query ? (
          <button
            type="button"
            className="site-header-mobile-search__clear"
            aria-label="Clear search"
            onMouseDown={(event) => event.preventDefault()}
            onClick={clearQuery}
          >
            <Icon name="XMarkIcon" size={16} />
          </button>
        ) : null}
        <button
          type="submit"
          aria-label="Search"
          className={
            mobile
              ? 'site-header-mobile-search__btn'
              : 'bg-[#e8471e] hover:bg-[#c73a17] text-white px-4 lg:px-8 py-2 lg:py-3 font-semibold text-sm rounded-r-lg flex items-center gap-1.5 shrink-0'
          }
        >
          <Icon name="MagnifyingGlassIcon" size={mobile ? 18 : 18} />
          {!mobile && <span className="hidden lg:inline">Search</span>}
        </button>
      </div>

      {show && (
        <div
          className={
            mobile
              ? 'site-header-mobile-search__dropdown'
              : 'absolute top-full left-0 right-0 mt-1 rounded-xl border border-gray-200 bg-white text-gray-800 shadow-xl z-50 overflow-hidden'
          }
        >
          <div role="status" className="text-xs text-gray-500 px-4 py-2.5 border-b border-gray-100">
            {status === 'loading'
              ? 'Searching products...'
              : status === 'error'
                ? 'Suggestions unavailable. Press Search to try again.'
                : items.length
                  ? 'Matching products'
                  : 'No matching products found'}
          </div>
          <ul
            id={listId}
            role="listbox"
            aria-label="Product suggestions"
            className="max-h-[min(20rem,50vh)] overflow-y-auto scrollbar-hide"
          >
            {items.map((product, index) => (
              <li
                key={product._id}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={selected === index}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setSelected(index)}
                onClick={() => go(product)}
                className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer text-sm min-h-[44px] ${
                  selected === index ? 'bg-blue-50' : 'hover:bg-gray-50 active:bg-blue-50'
                }`}
              >
                <Icon name="MagnifyingGlassIcon" size={18} className="shrink-0 text-gray-400" />
                <span className="min-w-0 break-words">{highlight(product.name)}</span>
              </li>
            ))}
          </ul>
          <button
            type="submit"
            className="w-full border-t border-gray-100 px-4 py-3.5 text-left text-sm font-semibold text-[#003087] hover:bg-blue-50 min-h-[44px]"
            onClick={(event) => {
              event.preventDefault();
              go();
            }}
          >
            View all results for “{term}”
          </button>
        </div>
      )}
    </form>
  );
}
